import { Form, Popover, Typography } from 'antd';
import { ReactComponent as IconQUestionMark } from 'assets/images/icons/iconQuestionMark.svg';
import { FormSelect } from 'components/Custom/selects';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import { getMappingSubsectionLabel } from 'i18n/mappingSectionLabel';
import { TaxRateOption } from 'pages/returns';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const SYSTEM_GENERATED = 'SYSTEM_GENERATED';

export type MappingBoxSelectRowProps = {
  subsectionId: string;
  subsectionName: string;
  subsectionCode: string;
  subsectionType?: string;
  hintText: string;
  fieldName: string;
  isOutOfScope: boolean;
  isOutOfScopeBox: boolean;
  isOtherSpecialBox: boolean;
  treatAsOutOfScope: boolean;
  options: TaxRateOption[];
  disabled: boolean;
  onRatesChange: (
    rateIds: string[],
    outOfScope: boolean,
    code: string
  ) => void;
  onSpecialSync?: (selectedIds: string[], selectedCode: string) => void;
};

function MappingBoxSelectRowComponent({
  subsectionId,
  subsectionName,
  subsectionCode,
  subsectionType,
  hintText,
  fieldName,
  isOutOfScope,
  isOutOfScopeBox,
  isOtherSpecialBox,
  treatAsOutOfScope,
  options,
  disabled,
  onRatesChange,
  onSpecialSync,
}: MappingBoxSelectRowProps) {
  const { t, i18n } = useTranslation();

  const filterOption = useCallback(
    (input: string, option?: { label?: string }) =>
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
    []
  );

  const maxTagPlaceholder = useCallback(
    (omittedValues: { label?: React.ReactNode }[]) => (
      <StyledTooltip //@ts-ignore
        styles={{
          root: {
            pointerEvents: 'none',
          },
        }}
        title={
          <>
            {omittedValues.map(({ label }, i) => (
              <React.Fragment key={`${String(label)}-${i}`}>
                <span>{label}</span>
                <br />
              </React.Fragment>
            ))}
          </>
        }
      >
        <span>
          +{formatDisplayInteger(omittedValues.length, i18n.language)}
        </span>
      </StyledTooltip>
    ),
    [i18n.language]
  );

  const handleChange = useCallback(
    (value: string[] | undefined) => {
      const rateIds = value ?? [];
      if (isOutOfScope && onSpecialSync) {
        onSpecialSync(rateIds, subsectionCode);
      }
      onRatesChange(rateIds, treatAsOutOfScope, subsectionCode);
    },
    [
      isOutOfScope,
      onRatesChange,
      onSpecialSync,
      subsectionCode,
      treatAsOutOfScope,
    ]
  );

  const boxClass = useMemo(() => {
    if (isOutOfScopeBox) return 'mapping-box-oos';
    if (isOtherSpecialBox) return 'mapping-box-other';
    return 'mapping-box';
  }, [isOutOfScopeBox, isOtherSpecialBox]);

  const label = getMappingSubsectionLabel(subsectionName, t);
  const codeLabel = getMappingSubsectionLabel(subsectionCode, t);

  return (
    <>
      <tr>
        <td className="mapping-label">
          {label}
          {hintText ? (
            <Popover
              placement="topLeft"
              trigger="hover"
              mouseEnterDelay={0.12}
              mouseLeaveDelay={0.35}
              destroyOnHidden={true}
              overlayStyle={{
                maxWidth: 'min(560px, calc(100vw - 32px))',
              }}
              styles={{
                body: { padding: 0 },
              }}
              content={
                <div
                  style={{
                    maxHeight: 'min(70vh, 520px)',
                    overflowY: 'auto',
                    padding: '14px 16px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.55,
                    fontSize: 13,
                    textAlign: 'left',
                  }}
                >
                  {hintText}
                </div>
              }
            >
              <span
                style={{
                  display: 'inline-flex',
                  cursor: 'pointer',
                }}
              >
                <IconQUestionMark
                  width={15}
                  height={15}
                  style={{ color: 'var(--color-primary, #41aaaf)' }}
                />
              </span>
            </Popover>
          ) : null}
        </td>
        <td className="box-column-space" />
        <td className="box-column" />
      </tr>
      <tr>
        <td>
          {subsectionType === SYSTEM_GENERATED ? (
            <div className="system-generated">
              <span>{t('vat-forms.totals')}</span>
              <span style={{ color: '#042A2A' }}>
                <i>{t('returns.mapping.system-generated')}</i>
              </span>
            </div>
          ) : (
            <Form.Item name={fieldName} style={{ marginBottom: 0 }}>
              <FormSelect //@ts-ignore
                className="mapping-tax-rate-select"
                width="100%"
                height="45px"
                mode="multiple"
                textalign={'start'}
                placeholder={t('common.select')}
                aria-label={subsectionName}
                options={options}
                onChange={handleChange}
                key={subsectionId}
                allowClear
                showSearch
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface, #f3f7f8)"
                selected_bg_color="var(--color-surface-raised, #ffffff)"
                radius="2px"
                maxTagCount="responsive"
                disabled={disabled}
                filterOption={filterOption}
                maxTagPlaceholder={maxTagPlaceholder}
              />
            </Form.Item>
          )}
        </td>
        <td className="box-column-space" />
        <td className="box-column">
          <div className={boxClass}>
            <Text>{codeLabel}</Text>
          </div>
        </td>
      </tr>
      <tr style={{ height: 25 }} />
    </>
  );
}

function propsAreEqual(
  prev: MappingBoxSelectRowProps,
  next: MappingBoxSelectRowProps
): boolean {
  return (
    prev.subsectionId === next.subsectionId &&
    prev.subsectionName === next.subsectionName &&
    prev.subsectionCode === next.subsectionCode &&
    prev.subsectionType === next.subsectionType &&
    prev.hintText === next.hintText &&
    prev.fieldName === next.fieldName &&
    prev.isOutOfScope === next.isOutOfScope &&
    prev.isOutOfScopeBox === next.isOutOfScopeBox &&
    prev.isOtherSpecialBox === next.isOtherSpecialBox &&
    prev.treatAsOutOfScope === next.treatAsOutOfScope &&
    prev.disabled === next.disabled &&
    prev.options === next.options &&
    prev.onRatesChange === next.onRatesChange &&
    prev.onSpecialSync === next.onSpecialSync
  );
}

export const MappingBoxSelectRow = React.memo(
  MappingBoxSelectRowComponent,
  propsAreEqual
);
